import { FREE_GIFT_IDS } from '@/components/blocks/FreeGiftV2/constants';
import { FreeGiftInteractionContext } from '@/components/blocks/FreeGiftV2/context/FreeGiftInteractionContext';
import { FreeGiftPromotionContext } from '@/components/blocks/FreeGiftV2/context/FreeGiftPromotionContext';
import { useFreeGiftPromotionCalculations } from '@/components/blocks/FreeGiftV2/hooks/useFreeGiftPromotionCalculations';
import { PromotionBlock } from '@/components/blocks/FreeGiftV2/parts/PromotionBlock';
import { PromotionFooter } from '@/components/blocks/FreeGiftV2/parts/PromotionFooter';
import { PromotionTitle } from '@/components/blocks/FreeGiftV2/parts/PromotionTitle';
import { SkuQuantity } from '@/components/blocks/FreeGiftV2/types';
import {
	clearFreeGiftSelection,
	clearStaleFreeGiftSelections,
	FREE_GIFT_ADDED_FROM_PDP_EVENT,
	FreeGiftAddedFromPdpEventDetail,
	readStoredFreeGiftSelection,
	saveFreeGiftSelection,
} from '@/components/blocks/FreeGiftV2/utils';
import { ProductType } from '@/data/types/Product';
import { CombinablePromotion } from '@/data/types/Promotions';
import { Box, Stack } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface FreeGiftsProps {
	items?: ProductType[];
	currentPartNumber: string;
	combinablePromotions?: CombinablePromotion[];
	isRewardView?: boolean;
}

export const FreeGift = (props: FreeGiftsProps) => {
	const {
		items: promoItems = [],
		currentPartNumber,
		combinablePromotions = [],
		isRewardView = false,
	} = props;

	const promotionContextValue = useFreeGiftPromotionCalculations({
		combinablePromotions,
		currentPartNumber,
		promoItems,
		isRewardView,
	});

	const [openDrawer, setOpenDrawer] = useState(false);
	const handleOpenDrawer = useCallback(() => setOpenDrawer(true), []);
	const handleCloseDrawer = useCallback(() => setOpenDrawer(false), []);

	const [isPromotionComplete, setIsPromotionComplete] = useState(false);
	const [hasAddedToCart, setHasAddedToCart] = useState(
		() => readStoredFreeGiftSelection(currentPartNumber)?.addedFromPdp ?? false
	);
	const [promotionQty, setPromotionQty] = useState<number>(
		() => readStoredFreeGiftSelection(currentPartNumber)?.promotionQty ?? 1
	);
	const [selectedPurchaseGroupId, setSelectedPurchaseGroupId] = useState<string | undefined>(
		() => readStoredFreeGiftSelection(currentPartNumber)?.selectedPurchaseGroupId
	);
	const [selectedPurchaseSkus, setSelectedPurchaseSkus] = useState<SkuQuantity[]>(
		() => readStoredFreeGiftSelection(currentPartNumber)?.selectedPurchaseSkus ?? []
	);
	const [selectedRewardGroupId, setSelectedRewardGroupId] = useState<string | undefined>(
		() => readStoredFreeGiftSelection(currentPartNumber)?.selectedRewardGroupId
	);
	const [selectedRewardSkus, setSelectedRewardSkus] = useState<SkuQuantity[]>(
		() => readStoredFreeGiftSelection(currentPartNumber)?.selectedRewardSkus ?? []
	);
	const [showPromotionOptions, setShowPromotionOptions] = useState(
		() => promotionContextValue.promotionGroups.length > 1
	);

	useEffect(() => {
		clearStaleFreeGiftSelections(currentPartNumber);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const handleAddedFromPdp = (event: Event) => {
			const detail = (event as CustomEvent<FreeGiftAddedFromPdpEventDetail>).detail;

			if (detail?.partNumber === currentPartNumber) {
				setHasAddedToCart(true);
			}
		};

		window.addEventListener(FREE_GIFT_ADDED_FROM_PDP_EVENT, handleAddedFromPdp);

		return () => window.removeEventListener(FREE_GIFT_ADDED_FROM_PDP_EVENT, handleAddedFromPdp);
	}, [currentPartNumber]);

	const { purchase, reward, selectedPromotionIndex, promotionGroups, promoThdCode } =
		promotionContextValue;

	useEffect(() => {
		const noPromotionSelected =
			promotionGroups.length > 1 && !promotionGroups[selectedPromotionIndex];

		if (!noPromotionSelected) {
			return;
		}

		if (isRewardView) {
			setSelectedRewardSkus([{ sku: currentPartNumber, quantity: 1 }]);
		} else {
			setSelectedPurchaseSkus([{ sku: currentPartNumber, quantity: 1 }]);
		}
	}, [promotionGroups, selectedPromotionIndex, isRewardView, currentPartNumber]);

	const isDirectPromotion =
		!purchase.combinable &&
		purchase.purchases.length === 1 &&
		!reward.combinable &&
		reward.rewards.length === 1;

	useEffect(() => {
		if (!isPromotionComplete) {
			clearFreeGiftSelection(currentPartNumber);
			return;
		}

		if (openDrawer) {
			return;
		}

		saveFreeGiftSelection(currentPartNumber, {
			selectedPurchaseGroupId,
			selectedPurchaseSkus,
			selectedRewardGroupId,
			selectedRewardSkus,
			promotionQty,
			isDirect: isDirectPromotion,
			promoThdCode,
		});
	}, [
		openDrawer,
		isPromotionComplete,
		currentPartNumber,
		selectedPurchaseGroupId,
		selectedPurchaseSkus,
		selectedRewardGroupId,
		selectedRewardSkus,
		promotionQty,
		isDirectPromotion,
		promoThdCode,
	]);

	const interactionContextValue = useMemo(
		() => ({
			isRewardView,
			openDrawer,
			handleOpenDrawer,
			handleCloseDrawer,
			isPromotionComplete,
			setIsPromotionComplete,
			hasAddedToCart,
			setHasAddedToCart,
			promotionQty,
			setPromotionQty,
			selectedPurchaseGroupId,
			setSelectedPurchaseGroupId,
			selectedPurchaseSkus,
			setSelectedPurchaseSkus,
			selectedRewardGroupId,
			setSelectedRewardGroupId,
			selectedRewardSkus,
			setSelectedRewardSkus,
			showPromotionOptions,
			setShowPromotionOptions,
		}),
		[
			isRewardView,
			openDrawer,
			handleOpenDrawer,
			handleCloseDrawer,
			isPromotionComplete,
			hasAddedToCart,
			promotionQty,
			selectedPurchaseGroupId,
			selectedPurchaseSkus,
			selectedRewardGroupId,
			selectedRewardSkus,
			showPromotionOptions,
		]
	);

	return (
		<FreeGiftPromotionContext.Provider value={promotionContextValue}>
			<FreeGiftInteractionContext.Provider value={interactionContextValue}>
				<Box>
					<Stack gap={'20px'} id={FREE_GIFT_IDS.block}>
						<PromotionTitle />
						<PromotionBlock />
						<PromotionFooter />
					</Stack>
				</Box>
			</FreeGiftInteractionContext.Provider>
		</FreeGiftPromotionContext.Provider>
	);
};
